import React, { useState, useEffect, useCallback } from 'react';
import { investmentPlanAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';

const AdminInvestmentPlans = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [businessRange, setBusinessRange] = useState('');
  const [activeRewardsOnly, setActiveRewardsOnly] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserPlan, setSelectedUserPlan] = useState(null);
  const [rewardForm, setRewardForm] = useState({
    amount: '',
    month: '',
    notes: ''
  });
  const [planForm, setPlanForm] = useState({
    planType: ''
  });

  // Investment plan configurations
  const investmentPlans = {
    '15000_1000_3': { investment: 15000, reward: 1000, duration: 3, name: 'Basic Plan' },
    '25000_3000_3': { investment: 25000, reward: 3000, duration: 3, name: 'Silver Plan' },
    '50000_5000_3': { investment: 50000, reward: 5000, duration: 3, name: 'Gold Plan' },
    '100000_6000_6': { investment: 100000, reward: 6000, duration: 6, name: 'Platinum Plan' },
    '250000_15000_6': { investment: 250000, reward: 15000, duration: 6, name: 'Diamond Plan' }
  };

  // Fetch initial data
  const fetchUsersBusiness = useCallback(async () => {
    try {
      setLoading(true);
      // Don't pass searchTerm to API anymore
      const params = {
        planType: selectedPlan,
        businessRange: businessRange,
        activeRewards: activeRewardsOnly
      };
      
      const response = await investmentPlanAPI.getUsersBusiness(params);
      if (response.data.success) {
        setUsers(response.data.data);
        setFilteredUsers(response.data.data);
      }
      setError('');
    } catch (err) {
      setError('Failed to fetch users business data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedPlan, businessRange, activeRewardsOnly]);

  // Apply client-side filtering whenever search term or users data changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }

    const lowercasedSearch = searchTerm.toLowerCase();
    const filtered = users.filter(user => 
      user.name?.toLowerCase().includes(lowercasedSearch) ||
      user.email?.toLowerCase().includes(lowercasedSearch) ||
      user.phone?.toLowerCase().includes(lowercasedSearch) ||
      user.mySponsorCode?.toLowerCase().includes(lowercasedSearch)
    );
    
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  useEffect(() => {
    fetchUsersBusiness();
    fetchSummary();
  }, [fetchUsersBusiness]);

  const fetchSummary = async () => {
    try {
      const response = await investmentPlanAPI.getSummary();
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  const handleApplyFilters = () => {
    fetchUsersBusiness();
  };

  const handleResetFilters = () => {
    setSelectedPlan('');
    setBusinessRange('');
    setActiveRewardsOnly(false);
    setSearchTerm('');
    // Refetch with reset filters
    setTimeout(() => {
      fetchUsersBusiness();
    }, 0);
  };

  const handleCreatePlan = async () => {
    if (!selectedUser || !planForm.planType) {
      setError('Please select user and plan type');
      return;
    }

    try {
      const response = await investmentPlanAPI.createPlan({
        userId: selectedUser._id,
        planType: planForm.planType
      });

      if (response.data.success) {
        setShowPlanModal(false);
        setPlanForm({ planType: '' });
        setSelectedUser(null);
        fetchUsersBusiness();
        fetchSummary();
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create plan');
    }
  };

  const handleDistributeReward = async () => {
    if (!selectedUserPlan || !rewardForm.amount || !rewardForm.month) {
      setError('Please fill all required fields');
      return;
    }

    try {
      const response = await investmentPlanAPI.distributeReward({
        userId: selectedUser._id,
        planId: selectedUserPlan._id,
        amount: parseFloat(rewardForm.amount),
        month: parseInt(rewardForm.month),
        notes: rewardForm.notes
      });

      if (response.data.success) {
        setShowRewardModal(false);
        setRewardForm({ amount: '', month: '', notes: '' });
        setSelectedUser(null);
        setSelectedUserPlan(null);
        fetchUsersBusiness();
        fetchSummary();
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to distribute reward');
    }
  };

  const openRewardModal = (user, plan) => {
    setSelectedUser(user);
    setSelectedUserPlan(plan);
    setRewardForm({
      amount: plan.monthlyReward.toString(),
      month: (plan.totalRewardsPaid / plan.monthlyReward + 1).toString(),
      notes: ''
    });
    setShowRewardModal(true);
  };

  const openPlanModal = (user) => {
    setSelectedUser(user);
    setPlanForm({ planType: '' });
    setShowPlanModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'text-green-600 bg-green-100';
      case 'COMPLETED': return 'text-blue-600 bg-blue-100';
      case 'CANCELLED': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading && users.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Investment Plans Management</h1>
        <p className="text-gray-600">Manage user investment plans and monthly rewards distribution</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Plans</h3>
            <p className="text-2xl font-bold text-gray-900">{summary.totalPlans}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Active Plans</h3>
            <p className="text-2xl font-bold text-green-600">{summary.activePlans}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Completed Plans</h3>
            <p className="text-2xl font-bold text-blue-600">{summary.completedPlans}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Rewards Paid</h3>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(summary.totalRewardsPaid)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search User</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name, Email, Phone or Sponsor Code"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type</label>
            <select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Plans</option>
              {Object.entries(investmentPlans).map(([key, plan]) => (
                <option key={key} value={key}>{plan.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business Range</label>
            <select
              value={businessRange}
              onChange={(e) => setBusinessRange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Ranges</option>
              <option value="0-15000">Below 15K</option>
              <option value="15000-25000">15K - 25K</option>
              <option value="25000-50000">25K - 50K</option>
              <option value="50000-100000">50K - 1L</option>
              <option value="100000-250000">1L - 2.5L</option>
              <option value="250000-">Above 2.5L</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center mb-2">
              <input
                type="checkbox"
                checked={activeRewardsOnly}
                onChange={(e) => setActiveRewardsOnly(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Active Rewards Only</span>
            </label>
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={handleApplyFilters}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Apply Filters
            </button>
            <button
              onClick={handleResetFilters}
              className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
        <div className="mt-2 text-sm text-gray-500">
          {searchTerm && (
            <span>Showing {filteredUsers.length} of {users.length} users matching "{searchTerm}"</span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sr. No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Direct Business
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Investment Plans
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Eligible Plans
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        <div className="text-sm text-gray-500">{user.phone}</div>
                        <div className="text-xs text-gray-400">{user.mySponsorCode}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(user.totalDirectBusiness)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Referral: {formatCurrency(user.referralBusiness)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Own: {formatCurrency(user.ownBusiness)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Direct: {user.directSponseesCount} members
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        {user.investmentPlans.map((plan) => (
                          <div key={plan._id} className="text-xs">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">
                                {investmentPlans[plan.planType]?.name || plan.planType}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(plan.status)}`}>
                                {plan.status}
                              </span>
                            </div>
                            <div className="text-gray-500">
                              {formatCurrency(plan.monthlyReward)} × {plan.durationMonths} months
                            </div>
                            <div className="text-gray-500">
                              Paid: {formatCurrency(plan.totalRewardsPaid)} / {formatCurrency(plan.monthlyReward * plan.durationMonths)}
                            </div>
                          </div>
                        ))}
                        {user.investmentPlans.length === 0 && (
                          <div className="text-xs text-gray-400">No active plans</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {user.eligiblePlans.map((plan, index) => (
                          <div key={index} className="text-xs text-green-600">
                            {plan.name}: {formatCurrency(plan.reward)}/mo × {plan.duration}mo
                          </div>
                        ))}
                        {user.eligiblePlans.length === 0 && (
                          <div className="text-xs text-gray-400">No eligible plans</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="space-y-2">
                        <button
                          onClick={() => openPlanModal(user)}
                          disabled={user.eligiblePlans.length === 0}
                          className={`w-full px-3 py-1 rounded text-xs transition-colors ${
                            user.eligiblePlans.length === 0
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          Create Plan
                        </button>
                        {user.investmentPlans
                          .filter(plan => plan.status === 'ACTIVE')
                          .map((plan) => (
                            <button
                              key={plan._id}
                              onClick={() => openRewardModal(user, plan)}
                              className="w-full bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 transition-colors"
                            >
                              Distribute Reward
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Plan Modal */}
      {showPlanModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Create Investment Plan</h2>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                User: {selectedUser.name} ({selectedUser.mySponsorCode})
              </p>
              <p className="text-sm text-gray-600">
                Direct Business: {formatCurrency(selectedUser.totalDirectBusiness)}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Plan</label>
              <select
                value={planForm.planType}
                onChange={(e) => setPlanForm({ ...planForm, planType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a plan</option>
                {selectedUser.eligiblePlans.map((plan) => (
                  <option key={plan.type} value={plan.type}>
                    {plan.name} - {formatCurrency(plan.reward)}/mo × {plan.duration}mo
                  </option>
                ))}
              </select>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCreatePlan}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Create Plan
              </button>
              <button
                onClick={() => {
                  setShowPlanModal(false);
                  setSelectedUser(null);
                  setPlanForm({ planType: '' });
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Distribute Reward Modal */}
      {showRewardModal && selectedUser && selectedUserPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Distribute Monthly Reward</h2>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                User: {selectedUser.name} ({selectedUser.mySponsorCode})
              </p>
              <p className="text-sm text-gray-600">
                Plan: {investmentPlans[selectedUserPlan.planType]?.name}
              </p>
              <p className="text-sm text-gray-600">
                Monthly Reward: {formatCurrency(selectedUserPlan.monthlyReward)}
              </p>
              <p className="text-sm text-gray-600">
                Next Month: {Math.floor(selectedUserPlan.totalRewardsPaid / selectedUserPlan.monthlyReward) + 1}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
              <input
                type="number"
                value={rewardForm.amount}
                onChange={(e) => setRewardForm({ ...rewardForm, amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
              <input
                type="number"
                value={rewardForm.month}
                onChange={(e) => setRewardForm({ ...rewardForm, month: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter month number (1, 2, 3...)"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
              <textarea
                value={rewardForm.notes}
                onChange={(e) => setRewardForm({ ...rewardForm, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Add any notes..."
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleDistributeReward}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
              >
                Distribute Reward
              </button>
              <button
                onClick={() => {
                  setShowRewardModal(false);
                  setSelectedUser(null);
                  setSelectedUserPlan(null);
                  setRewardForm({ amount: '', month: '', notes: '' });
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvestmentPlans;